import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format, subMonths } from 'date-fns';
import { fetchLogs } from '../store/slices/logsSlice';
import supervisorService from '../services/supervisorService';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';

export default function Logs() {
  const dispatch = useDispatch();
  const { logs, loading, pagination } = useSelector((state) => state.logs);
  const { user } = useSelector((state) => state.auth);

  const userRole = user?.role?.name || 'driver';
  const isDriver = userRole === 'driver';
  const showDriverColumn = userRole === 'admin' || userRole === 'supervisor';

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    driver_id: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [drivers, setDrivers] = useState([]);

  // Fetch team members for driver filter (supervisors/admins only)
  useEffect(() => {
    if (showDriverColumn) {
      const fetchDrivers = async () => {
        try {
          const response = await supervisorService.getTeamMembers();
          setDrivers(response.data || []);
        } catch (error) {
          console.error('Failed to fetch drivers:', error);
        }
      };
      fetchDrivers();
    }
  }, [showDriverColumn]);

  useEffect(() => {
    dispatch(fetchLogs(filters));
  }, [dispatch, filters]);

  const getPageTitle = () => {
    if (userRole === 'admin') return 'All Driver Logs';
    if (userRole === 'supervisor') return 'Team Driver Logs';
    return 'My Log Entries';
  };

  const getEmptyMessage = () => {
    if (userRole === 'admin') return 'No log entries in the system yet.';
    if (userRole === 'supervisor') return 'No log entries from your assigned drivers yet.';
    return 'Get started by creating your first log entry.';
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      driver_id: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== '');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-md border transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-primary-50 border-primary-600 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="px-2 py-0.5 text-xs bg-primary-600 text-white rounded-full">
                  {Object.values(filters).filter((v) => v !== '').length}
                </span>
              )}
            </div>
          </button>
          {isDriver && (
            <Link to="/logs/new" className="btn btn-primary">
              + New Entry
            </Link>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            {/* Driver Filter (Supervisors/Admins only) */}
            {showDriverColumn && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Driver
                </label>
                <select
                  value={filters.driver_id}
                  onChange={(e) => handleFilterChange('driver_id', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All Drivers</option>
                  {drivers.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.user?.first_name} {driver.user?.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Filter Actions */}
          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                {Object.values(filters).filter((v) => v !== '').length} filter(s) active
              </div>
              <button
                onClick={handleClearFilters}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No log entries</h3>
          <p className="mt-2 text-gray-600">{getEmptyMessage()}</p>
          {isDriver && (
            <Link to="/logs/new" className="mt-4 inline-block btn btn-primary">
              Create Entry
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {showDriverColumn && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shift
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trips
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  {showDriverColumn && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm">
                          {log.driver?.user?.first_name?.[0]?.toUpperCase() || 'D'}
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">
                            {log.driver?.user?.first_name} {log.driver?.user?.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">
                      {format(new Date(log.log_date), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {log.shift_start && log.shift_end
                      ? `${log.shift_start} - ${log.shift_end}`
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {log.total_distance ? `${log.total_distance} km` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {log.trips?.length || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={log.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-3">
                      {/* Show Edit for drivers (draft only) or supervisors/admins (all except rejected) */}
                      {((isDriver && log.status === 'draft') ||
                        (!isDriver && log.status !== 'rejected')) && (
                        <Link
                          to={`/logs/${log.id}`}
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Edit
                        </Link>
                      )}
                      {/* Show View for everyone, but only if not showing Edit, OR show both for supervisors/admins */}
                      {(!isDriver || log.status !== 'draft') && (
                        <Link
                          to={`/logs/${log.id}`}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {logs.length} of {pagination.total} entries
              </div>
              <div className="flex space-x-2">
                {/* Pagination buttons would go here */}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
